import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const getText = (en, pt) => language === 'pt' ? pt : en;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a5c3a] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            {getText('Back', 'Voltar')}
          </button>
          <h1 className="text-3xl font-bold">
            {getText('Privacy Policy', 'Política de Privacidade')}
          </h1>
          <p className="text-white/70 mt-2">
            {getText('Last updated: January 2025', 'Última atualização: Janeiro 2025')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('1. Introduction', '1. Introdução')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'Sancur Property Services ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.',
                'A Sancur Property Services ("nós", "nosso" ou "nos") está comprometida em proteger a sua privacidade. Esta Política de Privacidade explica como recolhemos, usamos, divulgamos e protegemos as suas informações quando utiliza o nosso website e serviços.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('2. Information We Collect', '2. Informações que Recolhemos')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {getText('We collect the following types of information:', 'Recolhemos os seguintes tipos de informação:')}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('Personal information (name, email, phone number, address)', 'Informações pessoais (nome, email, número de telefone, morada)')}</li>
              <li>{getText('Property details for service quotes', 'Detalhes da propriedade para orçamentos')}</li>
              <li>{getText('Payment information', 'Informações de pagamento')}</li>
              <li>{getText('Photos you upload of your property', 'Fotos que envia da sua propriedade')}</li>
              <li>{getText('Communication history with our team', 'Histórico de comunicação com a nossa equipa')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('3. How We Use Your Information', '3. Como Usamos as Suas Informações')}
            </h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('To provide and maintain our cleaning services', 'Para fornecer e manter os nossos serviços de limpeza')}</li>
              <li>{getText('To process your service requests and payments', 'Para processar os seus pedidos de serviço e pagamentos')}</li>
              <li>{getText('To communicate with you about appointments and services', 'Para comunicar consigo sobre agendamentos e serviços')}</li>
              <li>{getText('To send you service-related notifications', 'Para enviar notificações relacionadas com o serviço')}</li>
              <li>{getText('To improve our services based on your feedback', 'Para melhorar os nossos serviços com base no seu feedback')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('4. Data Security', '4. Segurança dos Dados')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',
                'Implementamos medidas de segurança técnicas e organizacionais apropriadas para proteger as suas informações pessoais contra acesso não autorizado, alteração, divulgação ou destruição.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('5. Your Rights (GDPR)', '5. Os Seus Direitos (RGPD)')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {getText('Under GDPR, you have the right to:', 'Ao abrigo do RGPD, tem o direito de:')}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('Access your personal data', 'Aceder aos seus dados pessoais')}</li>
              <li>{getText('Rectify inaccurate data', 'Retificar dados incorretos')}</li>
              <li>{getText('Request deletion of your data', 'Solicitar a eliminação dos seus dados')}</li>
              <li>{getText('Object to processing of your data', 'Opor-se ao tratamento dos seus dados')}</li>
              <li>{getText('Data portability', 'Portabilidade dos dados')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('6. Contact Us', '6. Contacte-nos')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'If you have any questions about this Privacy Policy, please contact us at:',
                'Se tiver alguma questão sobre esta Política de Privacidade, contacte-nos em:'
              )}
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-xl">
              <p className="text-gray-700 font-medium">Sancur Property Services</p>
              <p className="text-gray-600">Email: info@sancurservices.com</p>
              <p className="text-gray-600">Phone: +353 89 950 2499</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
